import { Link } from "react-router-dom";
import AlchamisLogo from "@/components/AlchamisLogo";
import NewsletterForm from "@/components/NewsletterForm";
import GlobalContactBlock from "@/components/GlobalContactBlock";
import { ALCHAMIS_PRIMARY_EMAIL } from "@/components/FlagIcon";
import { Mail, MapPin, Linkedin } from "lucide-react";
import { CONSULTING_SERVICES, SOLUTIONS } from "@/data/content";
import { INDUSTRY_PAGES } from "@/data/industries";

export default function Footer() {
  const practices = CONSULTING_SERVICES.slice(0, 8);
  const industries = INDUSTRY_PAGES.slice(0, 8);
  return (
    <footer data-testid="site-footer" className="bg-[#000B24] border-t border-gold/10 mt-32">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand block */}
          <div className="md:col-span-4">
            <div className="mb-6">
              <AlchamisLogo variant="full" height={80} tagline="Est. 2017 · Global" />
            </div>
            <p className="font-display text-2xl text-white/85 leading-snug text-balance max-w-sm">
              Where institutional discipline meets next-generation technology.
            </p>
            <div className="hairline my-8" />
            <ul className="space-y-3 text-sm text-white/65">
              <li className="flex items-start gap-3">
                <MapPin size={14} className="text-gold mt-1 shrink-0" />
                <div>
                  <div>90 E Halsey Rd, Suite 332</div>
                  <div>Parsippany, New Jersey 07054, USA</div>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={14} className="text-gold" />
                <a href={`mailto:${ALCHAMIS_PRIMARY_EMAIL}`} className="hover:text-white transition-colors" data-testid="footer-email">
                  {ALCHAMIS_PRIMARY_EMAIL}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Linkedin size={14} className="text-gold" />
                <a
                  href="https://www.linkedin.com/company/alchamisgroup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  LinkedIn — AlchamisGroup
                </a>
              </li>
            </ul>
            <div className="mt-6">
              <div className="overline mb-3">Global Lines</div>
              <GlobalContactBlock variant="footer" testIdPrefix="footer-phone" />
            </div>
            <div className="mt-8">
              <Link to="/contact" className="luxury-button-ghost text-[0.65rem]">Request Briefing</Link>
            </div>
          </div>

          {/* Practices */}
          <div className="md:col-span-2">
            <div className="overline mb-5">Consulting</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              {practices.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="hover:text-gold">{s.name}</Link>
                </li>
              ))}
              <li className="pt-2"><Link to="/services" className="text-gold/80 hover:text-gold">All 15 Practices →</Link></li>
              <li><Link to="/ai-services" className="text-gold/80 hover:text-gold">AI Services →</Link></li>
            </ul>
          </div>

          {/* Platforms */}
          <div className="md:col-span-2">
            <div className="overline mb-5">Platforms</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              {SOLUTIONS.map((s) => (
                <li key={s.slug}>
                  <Link to={`/platforms/${s.slug}`} className="hover:text-gold">{s.name}</Link>
                </li>
              ))}
              <li className="pt-2"><Link to="/platforms" className="text-gold/80 hover:text-gold">All Platforms →</Link></li>
            </ul>
          </div>

          {/* Industries */}
          <div className="md:col-span-2">
            <div className="overline mb-5">Industries</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              {industries.map((i) => (
                <li key={i.slug}>
                  <Link to={`/industries/${i.slug}`} className="hover:text-gold">{i.name}</Link>
                </li>
              ))}
              <li className="pt-2"><Link to="/industries" className="text-gold/80 hover:text-gold">All Industries →</Link></li>
            </ul>
          </div>

          {/* Firm + Newsletter */}
          <div className="md:col-span-2">
            <div className="overline mb-5">Firm</div>
            <ul className="space-y-2.5 text-sm text-white/70 mb-10">
              <li><Link to="/about" className="hover:text-gold">About</Link></li>
              <li><Link to="/case-studies" className="hover:text-gold">Case Studies</Link></li>
              <li><Link to="/insights" className="hover:text-gold">Insights</Link></li>
              <li><Link to="/ai-advisor" className="hover:text-gold">AI Advisor</Link></li>
              <li><Link to="/contact" className="hover:text-gold">Contact</Link></li>
              <li><Link to="/admin/login" className="hover:text-gold">Internal Portal</Link></li>
            </ul>
            <div className="overline mb-3">Briefings</div>
            <p className="text-white/55 text-xs mb-3 leading-relaxed">
              Quarterly executive memos.
            </p>
            <NewsletterForm source="footer" testIdPrefix="footer-newsletter" />
          </div>
        </div>

        <div className="hairline mt-16 mb-8" />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/45">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <span>© {new Date().getFullYear()} AlchamisGroup. All rights reserved.</span>
            <Link to="/contact" className="hover:text-gold">Privacy</Link>
            <Link to="/contact" className="hover:text-gold">Terms</Link>
            <Link to="/contact" className="hover:text-gold">Modern Slavery Statement</Link>
            <Link to="/contact" className="hover:text-gold">Responsible Disclosure</Link>
          </div>
          <div className="tracking-[0.22em] uppercase">www.AlchamisGroup.com</div>
        </div>
      </div>
    </footer>
  );
}
